import Customer from '@/classes/case/Customer'
import { Document } from '@/classes/case/Document'
import State from '@/classes/case/State'
import Report from '@/classes/certificate/Report'
import { InvalidStateError } from '../errors'
import { dateTimeFormat } from '@/utility/utility_functions'
import { API_ROOT } from '@/api/config'
import Service from '@/classes/case/Service'
import { Comment } from '@/classes/certificate/Comment'

class Certificate {
  static VALIDITY_PERIOD_UID = 'validity_period'
  static ISSUED_DATE_UID = 'issued_date'
  static EXPIRY_DATE_UID = 'expiry_date'
  static EDIT_CERTIFICATE = 1
  static CREATE_RENEWAL_CASE = 2
  static REVOKE_CERTIFICATE = 3
  static SUSPEND_CERTIFICATE = 9
  static UNSUSPEND_CERTIFICATE = 10
  static SUSPENSION_REGISTERED = 11
  static START_REVISE_CASE = 12

  constructor (id, certificateNumber, state, customer, issueDate, expiryDate,
               validityPeriod, filePDF, filePNG, reports, service, qualification,
               caseId, comment, reported, suspensionDate, suspensionExpiryDate,
               suspensionRegistered, caseReportState, reportData) {
    this.id = id
    this.certificateNumber = certificateNumber
    this.state = state
    this.customer = customer
    this.issueDate = issueDate
    this.expiryDate = expiryDate
    this.validityPeriod = validityPeriod
    this.filePDF = filePDF
    this.filePNG = filePNG
    this.reports = reports
    this.service = service
    this.qualification = qualification
    this.caseId = caseId
    this.comment = comment
    this.reported = reported
    this.suspensionDate = suspensionDate
    this.suspensionExpiryDate = suspensionExpiryDate
    this.suspensionRegistered = suspensionRegistered
    this.caseReportState = caseReportState
    this.reportData = reportData
  }

  static asCertificates (json) {
    return json.map(certificate => {
      return Certificate.asCertificate(certificate)
    })
  }

  static asCertificate (json) {
    return new Certificate(
      json.id,
      json.certificate_number,
      json.state ? CertificateState.asState(json.state) : null,
      json.owner ? Customer.asCustomer(json.owner) : {},
      json.issued_date ? dateTimeFormat(json.issued_date) : '',
      json.expiry_date ? dateTimeFormat(json.expiry_date) : '',
      json.validity_period,
      json.pdf ? Document.asDocument(json.pdf) : null,
      json.png ? Document.asDocument(json.png) : null,
      json.reportings ? Report.asReports(json.reportings) : [],
      json.content && json.content.service ? Service.asService(json.content.service) : null,
      json.content && json.content.qualification ? json.content.qualification : {},
      json.content && json.content.case_pk ? json.content.case_pk : null,
      json.comment ? Comment.asComment(json.comment) : {},
      json.is_reported,
      json.suspension_date ? dateTimeFormat(json.suspension_date) : '',
      json.suspension_expiry_date ? dateTimeFormat(json.suspension_expiry_date) : '',
      json.suspension_registered,
      json.latest_reporting_case_state,
      json.reporting_information ? CertificateReportData.asReportData(json.reporting_information) : null
    )
  }

  get self () {
    return { item: this }
  }
  get organisationName () {
    return this.customer.organisationName
  }

  get certificateLink () {
    return { name: 'ReportsUpcomingView', params: { certificateId: this.id } }
  }

  get certificateAdminLink () {
    return { name: 'ReportsUpcomingView', params: { certificateId: this.id } }
  }

  get certificateViewLink () {
    return { name: 'CertificateDetailView', params: { certificateId: this.id } }
  }

  static getCertificateLiveLink (id) {
    return `${API_ROOT}/certificates/cases/${id}/view/`
  }

  static getCertificateStaticLink (id) {
    return `${API_ROOT}/certificates/${id}/view/`
  }

  get certificateDownloadLink () {
    return `${API_ROOT}/certificates/download/${this.id}/`
  }

  get publicCertificateDownloadLink () {
    return `${API_ROOT}/public/certificates/download/${this.id}/`
  }

  static getDecisionLetterLiveLink (id) {
    return `${API_ROOT}/certificates/cases/${id}/decision-letter/view/`
  }

  static getDecisionLetterStaticLink (id) {
    return `${API_ROOT}/certificates/${id}/decision-letter/view/`
  }

  get decisionLetterDownloadLink () {
    return `${API_ROOT}/certificates/decision-letter/download/${this.id}/`
  }

  get isCertificateExpired () {
    return this.state.value === CertificateState.EXPIRED
  }

  get isCertificateSuspended () {
    return this.state.value === Certificate.SUSPEND_CERTIFICATE
  }

  get isSuspensionRegistered () {
    return this.suspensionRegistered
  }

  get isReportingHours () {
    return Boolean(this.reportData && this.reportData.totalNumberReportedHours)
  }

  get isReportingSimpleProjects () {
    return Boolean(this.reportData && this.reportData.totalNumberReportedSimpleProjects)
  }

  get isReportingComplexProjects () {
    return Boolean(this.reportData && this.reportData.totalNumberReportedComplexProjects)
  }

  get holder () {
    return this.customer.fullName
  }

  get publicHolder () {
    if (this.customer.publicCertificates) {
      return this.customer.fullName
    }
    return ''
  }

  get isReported () {
    return {
      display: this.reported ? 'Yes' : 'No',
      level: this.reported ? State.SUCCESS : State.DANGER,
      custom_classes: ['tag-flex']
    }
  }

  get isReportedCell () {
    return this.isReported
  }

  get hasComment () {
    // show comment if comment state is equal to certificate state
    return this.id && this.comment.text && this.comment.state.value === this.state.value
  }

  get isSuspended () {
    return this.state.value === CertificateState.SUSPENDED
  }

  updateCertificate (certificate) {
    if (this.id !== certificate.id) {
      throw new Error('Objects missmatch')
    }
    this.certificateNumber = certificate.certificateNumber
    this.state = certificate.state
    this.customer = certificate.customer
    this.issueDate = certificate.issueDate
    this.expiryDate = certificate.expiryDate
    this.validityPeriod = certificate.validityPeriod
    this.filePDF = certificate.filePDF
    this.filePNG = certificate.filePNG
    this.reports = certificate.reports
    this.service = certificate.service
    this.qualification = certificate.qualification
    this.reported = certificate.reported
    this.suspensionDate = certificate.suspensionDate
    this.suspensionExpiryDate = certificate.suspensionExpiryDate
    this.suspensionRegistered = certificate.suspensionRegistered
  }

  getPublicCertificateActions () {
    let actions = [
      {
        type: 'LINK',
        link: this.publicCertificateDownloadLink,
        text: `Download certificate`
      }
    ]
    return actions
  }

  getCertificateActions (_authUser, handleAction) {
    let actions = [
      {
        type: 'LINK',
        link: this.certificateDownloadLink,
        text: `Download certificate`
      },
      {
        type: 'LINK',
        link: this.decisionLetterDownloadLink,
        text: `Download decision letter`
      }
    ]
    if (!_authUser.isCustomer()) {
      actions.push({
        type: 'ROUTE',
        route: { name: 'ReviewReportFindingsView', params: { caseId: this.caseId } },
        text: 'View review report'
      })
    }
    if (_authUser.isManager()) {
      actions.push({
        type: 'ACTION',
        func () {
          handleAction(Certificate.EDIT_CERTIFICATE)
        },
        text: `Edit certificate`
      })
      if (CertificateState.VALID_STATES.includes(this.state.value) || this.isCertificateExpired) {
          actions.push({
            type: 'ACTION',
            func () {
              handleAction(Certificate.REVOKE_CERTIFICATE)
            },
            text: `Revoke certificate`
          })
      }
      if (!this.isCertificateExpired) {
        if (CertificateState.VALID_STATES.includes(this.state.value)) {
          actions.push({
            type: 'ACTION',
            func () {
              handleAction(Certificate.SUSPEND_CERTIFICATE)
            },
            text: `Suspend certificate...`
          })
        }
        if (this.state.value === CertificateState.PUBLISH || this.state.value === CertificateState.REVOKED) {
          actions.push({
            type: 'ACTION',
            func () {
              handleAction(Certificate.START_REVISE_CASE)
            },
            text: `Start revise case`
          })
        }
        if (this.isCertificateSuspended) {
          actions.push({
            type: 'ACTION',
            func () {
              handleAction(Certificate.UNSUSPEND_CERTIFICATE)
            },
            text: `Unsuspend certificate`
          })
        }
      }
    }
    if (_authUser.isAdministrator()) {
      actions.push({
        type: 'ACTION',
        func () {
          handleAction(Certificate.CREATE_RENEWAL_CASE)
        },
        text: `Create renewal case`
      })
      if (this.isCertificateSuspended && !this.isCertificateExpired && !this.isSuspensionRegistered) {
        actions.push({
          type: 'ACTION',
          func () {
            handleAction(Certificate.SUSPENSION_REGISTERED)
          },
          text: `Registration done`
        })
      }
    }
    return actions
  }
}

class CertificateState {
  static DRAFT = 1
  static PUBLISH = 2
  static REVOKED = 3
  static EXPIRED = 4
  static SUSPENDED = 9

  static VALID_STATES = [
    CertificateState.DRAFT,
    CertificateState.PUBLISH]

  constructor (value, display, level) {
    if (!CertificateState.VALID_STATES.includes(value)) throw new InvalidStateError()
    this.value = value
    this.display = display
    this.level = level
  }

  static asState (json) {
    return new State(
      json.value,
      json.display,
      json.level || 0
    )
  }
}

class CertificateReportData {
  constructor (totalNumberReportedHours, totalNumberReportedSimpleProjects, totalNumberReportedComplexProjects) {
    this.totalNumberReportedHours = totalNumberReportedHours
    this.totalNumberReportedSimpleProjects = totalNumberReportedSimpleProjects
    this.totalNumberReportedComplexProjects = totalNumberReportedComplexProjects
  }

  static asReportData (json) {
    return new CertificateReportData(
      json.total_number_reported_hours,
      json.total_number_reported_simple_projects,
      json.total_number_reported_complex_projects
    )
  }
}

class ServiceCodes {
  static POP = 2
}

class NullCertificateState {
  constructor () {
    this.value = null
    this.display = 'Missing'
    this.level = 0
  }
}

export {
  Certificate,
  CertificateState,
  CertificateReportData,
  ServiceCodes,
  NullCertificateState
}
